import FakeInput from 'components/Common/Form/Input/FakeInput'
import type { OfferPagePriceMode } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions, offerPagePriceModes } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useToggle from 'hooks/useToggle'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import React, { useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'

interface Props {
  priceMode: OfferPagePriceMode
}

function usePriceModeLabels() {
  const intl = useIntl()
  const totalLabel = intl.formatMessage({
    id: 'kdRAw9To7phygq6avApQ',
    defaultMessage: 'Total price',
    description: 'Form field label for total price mode in price mode selector',
  })
  const perPersonLabel = intl.formatMessage({
    id: 'Dm54qYskIq4z3mInhSp1',
    defaultMessage: 'Price per person',
    description: 'Label for per-person price mode in price mode selector',
  })
  return { total: totalLabel, perPerson: perPersonLabel } as Record<OfferPagePriceMode, string>
}

function PriceModeSelectDesktop({ priceMode }: Props) {
  const intl = useIntl()
  const { value: isOpen, toggle, off: close } = useToggle()
  const anchorRef = useRef<HTMLLabelElement>(null)
  const dispatch = useOfferPageDispatch()
  const priceModeLabels = usePriceModeLabels()

  const setMode = useCallback((newMode: OfferPagePriceMode) => {
    dispatch({ type: OfferPageActions.setPriceMode, priceMode: newMode })
    close()
  }, [dispatch, close])

  const showLabel = intl.formatMessage(
    {
      id: 'dTl1PJEtye1etuKWmBsR',
      defaultMessage: 'Show: {priceModeLabel}',
      description: 'Prefix label with selected price mode in price mode selector',
    },
    { priceModeLabel: priceModeLabels[priceMode] },
  )

  return <>
    <FakeInput
      ref={anchorRef}
      onClick={toggle}
      value={showLabel}
      endIcon={<LineAngleDownIcon />}
      noValidationSpacing
    />
    <DropdownList size="S" open={isOpen} onClose={close} anchorRef={anchorRef} triggerRef={anchorRef} placement="bottom-end">
      {offerPagePriceModes.map(mode => <ClickableListItem key={mode} title={priceModeLabels[mode]} onClick={() => setMode(mode)} />)}
    </DropdownList>
  </>
}

type MappedProps = Pick<Props, 'priceMode'>

export default withOfferPageState((state): MappedProps => ({
  priceMode: state.priceMode,
}), PriceModeSelectDesktop)
